import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core'
import { useSensor, useSensors } from '@dnd-kit/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { TripStop } from 'tripPlanner/api/types'
import {
  POINTER_DRAG_ACTIVATION_DISTANCE,
  TOUCH_DRAG_ACTIVATION_DELAY,
  TOUCH_DRAG_ACTIVATION_TOLERANCE,
  TimelinePointerSensor,
  TimelineTouchSensor,
  type PointerCoordinates,
  type TimelineDropPlacement,
  type TimelineDropTarget,
  type TimelineItem,
  type TimelineMove,
  applyOptimisticTimelineMove,
  buildTimelineItemById,
  buildTimelineItemLocationById,
  buildTimelineMove,
  canDragTimelineItem,
  getDropPointFromDragEvent,
  getPointerCoordinates,
  getTimelineDropTargetFromPoint,
  isSameTimelineDropTarget,
} from './tripPlanCanvasReorder'

interface UseTripPlanCanvasDragReorderParams {
  stops: Array<TripStop>
  disabled: boolean
  onMoveItem: (itemId: number, move: TimelineMove) => void | boolean | Promise<void | boolean>
}

interface UseTripPlanCanvasDragReorderResult {
  /** Stops with any in-flight optimistic move applied; render from this. */
  orderedStops: Array<TripStop>
  activeItem: TimelineItem | undefined
  overItemId: number | undefined
  dropPlacement: TimelineDropPlacement | undefined
  overDayId: number | undefined
  overUnplannedStopId: number | undefined
  isDragActive: boolean
  sensors: ReturnType<typeof useSensors>
  handleDragStart: (event: DragStartEvent) => void
  handleDragMove: (event: DragMoveEvent) => void
  handleDragEnd: (event: DragEndEvent) => void
  handleDragCancel: () => void
}

export function useTripPlanCanvasDragReorder({
  stops,
  disabled,
  onMoveItem,
}: UseTripPlanCanvasDragReorderParams): UseTripPlanCanvasDragReorderResult {
  const [activeItemId, setActiveItemId] = useState<number | undefined>(undefined)
  const [overItemId, setOverItemId] = useState<number | undefined>(undefined)
  const [dropPlacement, setDropPlacement] = useState<TimelineDropPlacement | undefined>(undefined)
  const [overDayId, setOverDayId] = useState<number | undefined>(undefined)
  const [overUnplannedStopId, setOverUnplannedStopId] = useState<number | undefined>(undefined)
  const [isDragActive, setIsDragActive] = useState(false)
  const [optimisticStops, setOptimisticStops] = useState<Array<TripStop> | undefined>(undefined)

  const dragOriginRef = useRef<PointerCoordinates | undefined>(undefined)
  const dragCenterOffsetRef = useRef<PointerCoordinates | undefined>(undefined)
  const pointerPositionRef = useRef<PointerCoordinates | undefined>(undefined)
  const activeItemIdRef = useRef<number | undefined>(undefined)
  const sourceDayIdRef = useRef<number | undefined>(undefined)
  const dropTargetRef = useRef<TimelineDropTarget | undefined>(undefined)
  const moveSequenceRef = useRef(0)

  const itemById = useMemo(() => buildTimelineItemById(stops), [stops])
  const itemLocationById = useMemo(() => buildTimelineItemLocationById(stops), [stops])

  const sensors = useSensors(
    useSensor(TimelinePointerSensor, {
      activationConstraint: {
        distance: POINTER_DRAG_ACTIVATION_DISTANCE,
      },
    }),
    useSensor(TimelineTouchSensor, {
      activationConstraint: {
        delay: TOUCH_DRAG_ACTIVATION_DELAY,
        tolerance: TOUCH_DRAG_ACTIVATION_TOLERANCE,
      },
    }),
  )

  useEffect(() => {
    setOptimisticStops(undefined)
  }, [stops])

  const getPointerDropPoint = useCallback((): PointerCoordinates | undefined => {
    const pointer = pointerPositionRef.current
    if (!pointer) return undefined

    const centerOffset = dragCenterOffsetRef.current
    return {
      x: pointer.x + (centerOffset?.x ?? 0),
      y: pointer.y + (centerOffset?.y ?? 0),
    }
  }, [])

  const resetDragState = useCallback(() => {
    setActiveItemId(undefined)
    setOverItemId(undefined)
    setDropPlacement(undefined)
    setOverDayId(undefined)
    setOverUnplannedStopId(undefined)
    setIsDragActive(false)
    activeItemIdRef.current = undefined
    sourceDayIdRef.current = undefined
    dropTargetRef.current = undefined
    dragOriginRef.current = undefined
    dragCenterOffsetRef.current = undefined
    pointerPositionRef.current = undefined
  }, [])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const itemId = Number(event.active.id)
    const item = Number.isFinite(itemId) ? itemById.get(itemId) : undefined
    if (!item || disabled || !canDragTimelineItem(item)) {
      resetDragState()
      return
    }

    setOverItemId(undefined)
    setDropPlacement(undefined)
    setOverDayId(undefined)
    setOverUnplannedStopId(undefined)
    setIsDragActive(true)
    setActiveItemId(itemId)
    activeItemIdRef.current = itemId
    sourceDayIdRef.current = itemLocationById.get(itemId)?.dayId
    dropTargetRef.current = undefined
    dragOriginRef.current = getPointerCoordinates(event.activatorEvent)
    dragCenterOffsetRef.current = undefined
    // Leave the live pointer position unset until a real pointer/touch event fires.
    // Seeding it with the drag origin would make a fast drag→drop resolve from the
    // pickup point (getPointerDropPoint short-circuits the translated-rect fallback)
    // if the move/end handler runs before the first pointer event.
    pointerPositionRef.current = undefined

    const initialRect = event.active.rect.current.initial
    if (initialRect && dragOriginRef.current) {
      dragCenterOffsetRef.current = {
        x: initialRect.left + initialRect.width / 2 - dragOriginRef.current.x,
        y: initialRect.top + initialRect.height / 2 - dragOriginRef.current.y,
      }
    }
  }, [disabled, itemById, itemLocationById, resetDragState])

  const resolveDropTarget = useCallback((dropPoint: PointerCoordinates): TimelineDropTarget | undefined => {
    const target = getTimelineDropTargetFromPoint(dropPoint, activeItemIdRef.current)
    // Appending to the day the item already lives in isn't a meaningful move, so
    // it isn't a valid target (a specific-item drop within that day still is).
    if (target?.type === 'day' && target.dayId === sourceDayIdRef.current) return undefined
    return target
  }, [])

  const updateDropTargetFromPoint = useCallback((dropPoint: PointerCoordinates) => {
    const nextTarget = resolveDropTarget(dropPoint)
    if (isSameTimelineDropTarget(nextTarget, dropTargetRef.current)) return

    dropTargetRef.current = nextTarget
    setOverItemId(nextTarget?.type === 'item' ? nextTarget.itemId : undefined)
    setDropPlacement(nextTarget?.type === 'item' ? nextTarget.placement : undefined)
    setOverDayId(nextTarget?.type === 'day' ? nextTarget.dayId : undefined)
    setOverUnplannedStopId(nextTarget?.type === 'unplanned' ? nextTarget.stopId : undefined)
  }, [resolveDropTarget])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const dropPoint = getPointerDropPoint() ?? getDropPointFromDragEvent(event, dragOriginRef.current, dragCenterOffsetRef.current)
    if (!dropPoint) return
    updateDropTargetFromPoint(dropPoint)
  }, [getPointerDropPoint, updateDropTargetFromPoint])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const dropPoint = getPointerDropPoint() ?? getDropPointFromDragEvent(event, dragOriginRef.current, dragCenterOffsetRef.current)
    const itemId = activeItemIdRef.current
    const sourceDayId = sourceDayIdRef.current
    const target = dropPoint ? getTimelineDropTargetFromPoint(dropPoint, itemId) : undefined
    resetDragState()

    if (disabled || itemId === undefined || !target) return

    const item = itemById.get(itemId)
    if (!item || !canDragTimelineItem(item)) return

    const move = buildTimelineMove(itemId, target, sourceDayId, itemLocationById)
    if (!move) return

    // Optimistically relocate the item so the drop lands where the insertion line
    // showed, then roll back if the move fails and no newer drag has superseded it.
    // Build on any in-flight optimistic order (not just canonical stops) so a second
    // drop before the first refetch lands keeps the earlier move visible.
    const previousOptimisticStops = optimisticStops
    setOptimisticStops(prev => applyOptimisticTimelineMove(prev ?? stops, itemId, target))
    const moveToken = ++moveSequenceRef.current
    const rollback = () => {
      if (moveSequenceRef.current !== moveToken) return
      setOptimisticStops(previousOptimisticStops)
    }

    Promise.resolve(onMoveItem(itemId, move)).then(moved => {
      if (moved === false) rollback()
    }).catch(rollback)
  }, [disabled, getPointerDropPoint, itemById, itemLocationById, onMoveItem, optimisticStops, resetDragState, stops])

  const handleDragCancel = useCallback(() => {
    resetDragState()
  }, [resetDragState])

  // Track the real pointer position and recompute the drop target on scroll, so the
  // indicator stays under the drag overlay even when the canvas scrolls (the pointer
  // is stationary during a scroll but the tiles move under it).
  useEffect(() => {
    if (!isDragActive) return

    const recompute = () => {
      const dropPoint = getPointerDropPoint()
      if (dropPoint) updateDropTargetFromPoint(dropPoint)
    }

    const handlePointerMove = (event: PointerEvent | TouchEvent) => {
      const coordinates = getPointerCoordinates(event)
      if (coordinates) pointerPositionRef.current = coordinates
      recompute()
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('touchmove', handlePointerMove, { passive: true })
    // Scroll events don't bubble; capture-phase catches the nested scroll container.
    window.addEventListener('scroll', recompute, true)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('touchmove', handlePointerMove)
      window.removeEventListener('scroll', recompute, true)
    }
  }, [getPointerDropPoint, isDragActive, updateDropTargetFromPoint])

  const activeItem = activeItemId !== undefined ? itemById.get(activeItemId) : undefined

  return {
    orderedStops: optimisticStops ?? stops,
    activeItem,
    overItemId,
    dropPlacement,
    overDayId,
    overUnplannedStopId,
    isDragActive,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  }
}
